import { openPresets } from '@/components/common/pill/preset'

import type { PillPreset } from '@/components/common/pill/preset'
import type { Verdict } from '@/features/air/case-management/types'

/**
 * The one owner of case-conclusion labels and colours (ADR 0020 §5).
 *
 * A conclusion is the verdict AIR®'s investigation reached — `benign` or
 * `malicious` today. It lives at the AIR **Platform** root, beside
 * `case-status-preset.ts` and for the same reason: two subfeatures render it
 * (case management's verdict banner, the overview's breakdown donut), and a
 * second copy is how "malicious" ends up red in a table and blue in the chart
 * directly above it.
 *
 * There is no third member. Uncertainty is carried by `confidence` and
 * `behavioral_fit`, and the **absence** of a conclusion is not a kind of
 * conclusion — it is a gap, rendered in the `neutral` tone outside the ring
 * (ADR 0020 §6). Do not add an "Inconclusive" preset here.
 */

/**
 * Annotated `Record<Verdict, …>` so `tsc` fails on a missing member the day the
 * union gains one — ADR 0020 §5's closed-union clause.
 */
const PRESETS: Record<Verdict, PillPreset> = {
	benign: { label: 'Benign', tone: 'success' },
	malicious: { label: 'Malicious', tone: 'error' },
}

/**
 * Opened for lookup on top of that exhaustive record — the second half of ADR
 * 0020 §5's idiom, and load-bearing here rather than defensive.
 *
 * The backend's `InvestigationVerdict` is **designed to grow**: the column is
 * unconstrained, and a verdict this record has never heard of must render as a
 * grey slice rather than vanish from a donut whose centre claims to equal its
 * slices.
 */
export const CASE_CONCLUSION_PRESETS = openPresets('case conclusion', PRESETS)

/**
 * How the **absence** of a conclusion reads, wherever a surface has to draw one
 * per row rather than as a figure beside a ring.
 *
 * Deliberately outside `PRESETS`: it is not a verdict, so it must never be
 * reachable by a lookup keyed on one, and it must never be a slice (ADR 0020
 * §6). Grey rather than amber for the same reason — AIR® was not *unsure*,
 * there is simply nothing to report yet.
 *
 * **Not shared with the case list's investigation chip, deliberately.** That
 * chip can tell an investigation that never ran from one that ran and reached
 * no verdict, and keeps the two apart (`investigation-display.ts`). The
 * overview cannot: its payload carries a conclusion or `null`, with nothing to
 * say which. This wording is the one that reads true for both.
 */
export const NO_CONCLUSION_PRESET: PillPreset = {
	label: 'No Investigation',
	tone: 'neutral',
}
